import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Users, Calendar, DollarSign, FileText, TrendingUp, Clock } from 'lucide-react';

const Dashboard = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const role = user?.role;
  const isStaff = role === 'admin' || role === 'hr_manager';
  const [stats, setStats] = useState(null);
  const [paidLeave, setPaidLeave] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Only fetch if we have a token
    const token = localStorage.getItem('token');
    if (token) {
      fetchDashboardData();
      // Non-staff roles (employee / HOD) see their own paid-leave balance
      if (!isStaff) {
        apiClient.get('/api/leave/paid-balance')
          .then((res) => setPaidLeave(res.data))
          .catch(() => setPaidLeave(null));
      }
      const interval = setInterval(fetchDashboardData, 30000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await apiClient.get('/api/reports/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'payroll': return <DollarSign size={14} className="text-[#00B8B8]" />;
      case 'employee': return <Users size={14} className="text-[#0EA5E9]" />;
      case 'leave': return <FileText size={14} className="text-[#F59E0B]" />;
      case 'attendance': return <Calendar size={14} className="text-[#10B981]" />;
      default: return <Clock size={14} className="text-[hsl(215_10%_70%)]" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: 'Total Employees',
      value: stats?.total_employees || 0,
      icon: Users,
      color: '#00B8B8',
      bgColor: 'rgba(0, 184, 184, 0.1)'
    },
    {
      title: 'Departments',
      value: stats?.total_departments || 0,
      icon: TrendingUp,
      color: '#0EA5E9',
      bgColor: 'rgba(14, 165, 233, 0.1)'
    },
    {
      title: 'Pending Leaves',
      value: stats?.pending_leaves || 0,
      icon: FileText,
      color: '#F59E0B',
      bgColor: 'rgba(245, 158, 11, 0.1)'
    },
    {
      title: 'Payroll This Month',
      value: `₹${stats?.payroll_this_month?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: '#10B981',
      bgColor: 'rgba(16, 185, 129, 0.1)'
    }
  ];

  return (
    <Layout>
      <div className="page-header">
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Dashboard</h1>
        <p className={`mt-1 ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-600'}`}>Welcome to Danwei HR</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={stat.title} className="stat-card card-hover" data-testid={`stat-card-${index}`}>
              <div className="flex items-center justify-between mb-4">
                <div style={{ backgroundColor: stat.bgColor }} className="w-12 h-12 rounded-lg flex items-center justify-center">
                  <Icon size={24} style={{ color: stat.color }} />
                </div>
              </div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>{stat.title}</p>
              <p className={`text-3xl font-bold mt-2 font-mono ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Quick Actions (Admin/HR only) OR Paid Leave balance (Employee/HOD) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isStaff ? (
        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-[hsl(220_6%_8%)] border-[hsl(215_10%_22%)]' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Quick Actions</h3>
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/employees')}
              className={`w-full flex items-center gap-3 p-4 rounded-lg transition-colors duration-150 text-left cursor-pointer border ${isDarkMode ? 'bg-[hsl(215_8%_20%)] hover:bg-[hsl(215_8%_24%)] border-[hsl(215_10%_22%)] hover:border-[#00B8B8]/50' : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-[#00B8B8]/50'}`}
            >
              <Users size={20} className="text-[#00B8B8]" />
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Manage Employees</p>
                <p className={`text-xs ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>View and edit employee profiles</p>
              </div>
            </button>
            <button 
              onClick={() => navigate('/attendance')}
              className={`w-full flex items-center gap-3 p-4 rounded-lg transition-colors duration-150 text-left cursor-pointer border ${isDarkMode ? 'bg-[hsl(215_8%_20%)] hover:bg-[hsl(215_8%_24%)] border-[hsl(215_10%_22%)] hover:border-[#00B8B8]/50' : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-[#00B8B8]/50'}`}
            >
              <Calendar size={20} className="text-[#00B8B8]" />
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Upload Attendance</p>
                <p className={`text-xs ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>Upload ESSL biometric data</p>
              </div>
            </button>
            <button 
              onClick={() => navigate('/payroll')}
              className={`w-full flex items-center gap-3 p-4 rounded-lg transition-colors duration-150 text-left cursor-pointer border ${isDarkMode ? 'bg-[hsl(215_8%_20%)] hover:bg-[hsl(215_8%_24%)] border-[hsl(215_10%_22%)] hover:border-[#00B8B8]/50' : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-[#00B8B8]/50'}`}
            >
              <DollarSign size={20} className="text-[#00B8B8]" />
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Process Payroll</p>
                <p className={`text-xs ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>Run monthly payroll</p>
              </div>
            </button>
          </div>
        </div>
        ) : (
        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-[hsl(220_6%_8%)] border-[hsl(215_10%_22%)]' : 'bg-white border-gray-200'}`} data-testid="paid-leave-balance-card">
          <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>My Paid Leave</h3>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-[#CCFBF1] flex items-center justify-center">
              <Calendar size={26} className="text-[#0F766E]" />
            </div>
            <div>
              <p className="text-4xl font-bold font-mono text-[#0F766E]" data-testid="paid-leave-available">
                {paidLeave ? paidLeave.available_paid_leaves : 0}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>paid leave day(s) available</p>
            </div>
          </div>
          <div className={`mt-4 pt-4 border-t grid grid-cols-2 gap-3 text-sm ${isDarkMode ? 'border-[hsl(215_10%_22%)]' : 'border-gray-200'}`}>
            <div>
              <p className={`${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>Earned</p>
              <p className={`font-semibold font-mono ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>{paidLeave ? paidLeave.earned_paid_leaves : 0}</p>
            </div>
            <div>
              <p className={`${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>Used</p>
              <p className={`font-semibold font-mono ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>{paidLeave ? paidLeave.used_paid_leaves : 0}</p>
            </div>
          </div>
          <p className={`mt-3 text-xs ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>Earn 1 paid leave for every 20 payable days worked.</p>
        </div>
        )}

        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-[hsl(220_6%_8%)] border-[hsl(215_10%_22%)]' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Recent Activity</h3>
          <div className="space-y-4">
            {stats?.recent_activities && stats.recent_activities.length > 0 ? (
              stats.recent_activities.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${isDarkMode ? 'bg-[hsl(215_8%_20%)]' : 'bg-gray-100'}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>
                      {activity.message}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>{getTimeAgo(activity.time)}</p>
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[rgba(0,184,184,0.1)] rounded-full flex items-center justify-center mt-0.5">
                    <DollarSign size={14} className="text-[#00B8B8]" />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Payroll processed for {stats?.payroll_count || 0} employees</p>
                    <p className={`text-xs ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>Just now</p>
                  </div>
                </div>
                {stats?.pending_leaves > 0 && (
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[rgba(245,158,11,0.1)] rounded-full flex items-center justify-center mt-0.5">
                      <FileText size={14} className="text-[#F59E0B]" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>{stats.pending_leaves} leave requests pending approval</p>
                      <p className={`text-xs ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-500'}`}>Pending</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;